import { DEFAULT_BALANCING_V1_CONFIG } from "./balancingV1";
import { DEFAULT_CAPACITY_PAYMENTS_CONFIG } from "./capacityPayments";
import { DEFAULT_GUARANTEES_OF_ORIGIN_CONFIG } from "./guaranteesOfOrigin";

export const FIXED_CAPACITY_FEE_INPUT_MODES = [
  { value: "eur_per_mw_year", label: "EUR/MW/year" },
  { value: "eur_per_mw_month", label: "EUR/MW/month" },
  { value: "eur_per_kw_month", label: "EUR/kW/month" },
  { value: "absolute_per_year", label: "Absolute EUR/year" },
  { value: "absolute_per_settlement_period", label: "Absolute EUR/settlement period" },
];

export const FIXED_CAPACITY_SETTLEMENT_FREQUENCIES = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semiannual", label: "Semiannual" },
  { value: "annual", label: "Annual" },
];

export const FIXED_CAPACITY_PENALTY_MODES = [
  { value: "none", label: "None" },
  { value: "fixed_annual_eur", label: "Fixed annual penalty" },
  { value: "percent_of_gross_fee", label: "% of gross annual fee" },
  { value: "fixed_per_settlement_period", label: "Fixed penalty per settlement period" },
];

export const MERCHANT_REVENUE_RECIPIENT_OPTIONS = [
  { value: "trader_offtaker", label: "Trader/offtaker" },
  { value: "project_owner", label: "Project owner" },
  { value: "shared", label: "Shared" },
  { value: "not_specified", label: "Not specified" },
];

export const POST_CONTRACT_REVENUE_MODE_OPTIONS = [
  { value: "merchant_reversion", label: "Project reverts to merchant revenue" },
  { value: "zero", label: "No post-contract revenue assumed" },
  { value: "continue_fixed_fee", label: "Continue fixed fee after modelled contract term" },
];

export const DEFAULT_TOLLING_FIXED_CAPACITY = {
  feeInputMode: "eur_per_mw_year",
  feeAmount: null,
  settlementFrequency: "monthly",
  contractTermYears: 10,
  annualEscalationPct: 0,
  capacityBasis: "installed_power_mw",
  contractedCapacityMw: null,
  applyAvailabilityAdjustment: true,
  availabilityAdjustmentPct: 95,
  penaltyMode: "none",
  annualPenaltyEur: null,
  penaltyPctOfGrossFee: null,
  penaltyPerSettlementPeriodEur: null,
  merchantOptimization: true,
  merchantValueBasis: "net_after_charging_cost",
  merchantRevenueRecipient: "trader_offtaker",
  postContractRevenueMode: "merchant_reversion",
  includedRevenueStreams: {
    dam: true,
    intraday: false,
    ida1: false,
    ida2: false,
    ida3: false,
    balancing: false,
    ancillary: false,
    capacity: false,
    go: false,
    other: false,
  },
  balancingV1: DEFAULT_BALANCING_V1_CONFIG,
  capacityPayments: DEFAULT_CAPACITY_PAYMENTS_CONFIG,
  guaranteesOfOrigin: DEFAULT_GUARANTEES_OF_ORIGIN_CONFIG,
  bankability: {
    counterpartyStatus: "generic_assumption",
    counterpartyType: "not_specified",
    counterpartyName: "",
    creditSupportType: "none",
    creditSupportMonths: null,
  },
  performanceRegime: {
    guaranteedAvailabilityPct: null,
    penaltyCapPctOfAnnualFee: null,
    plannedOutageAllowanceHours: null,
    guaranteedRtePct: null,
    maxAnnualCycles: null,
    maxAnnualThroughputMWh: null,
    degradationResponsibility: "not_specified",
    augmentationResponsibility: "not_specified",
    chargingCostResponsibility: "trader_offtaker",
    dispatchRights: "trader_offtaker",
  },
  fixedCapacityFeePreview: null,
};

const VALID_FEE_INPUT_MODES = new Set(FIXED_CAPACITY_FEE_INPUT_MODES.map((mode) => mode.value));
const VALID_PENALTY_MODES = new Set(FIXED_CAPACITY_PENALTY_MODES.map((mode) => mode.value));

function parseFiniteNumber(value, fallback = 0) {
  if (value === "" || value == null) return fallback;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function parseOptionalFiniteNumber(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function clampPercent(value, fallback = 0) {
  const parsed = parseFiniteNumber(value, fallback);
  return Math.min(100, Math.max(0, parsed));
}

function hasOwnValue(source, key) {
  return Object.prototype.hasOwnProperty.call(source || {}, key);
}

function normalizeSettlementFrequency(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "quarterly" || normalized === "quarter") return "quarterly";
  if (normalized === "semiannual" || normalized === "semi_annually" || normalized === "semi-annually") {
    return "semiannual";
  }
  if (normalized === "annually" || normalized === "annual" || normalized === "yearly") return "annual";
  return "monthly";
}

export function getFixedCapacitySettlementPeriodsPerYear(settlementFrequency) {
  switch (normalizeSettlementFrequency(settlementFrequency)) {
    case "annual":
      return 1;
    case "semiannual":
      return 2;
    case "quarterly":
      return 4;
    case "monthly":
    default:
      return 12;
  }
}

function normalizeCapacityBasis(value) {
  const normalized = String(value || "").trim();
  if (normalized === "ContractedMW" || normalized === "contracted_capacity_mw") {
    return "contracted_capacity_mw";
  }
  return "installed_power_mw";
}

function toLegacyCapacityBasis(value) {
  return value === "contracted_capacity_mw" ? "ContractedMW" : "InstalledMW";
}

export function deriveInstalledBessPowerMw(config = {}) {
  const selected = Array.isArray(config?.bess?.selected) ? config.bess.selected : [];
  const totalKw = selected.reduce((sum, battery) => {
    const count = parseFiniteNumber(battery?.count, 1);
    const powerKw = parseFiniteNumber(
      battery?.p_max_d ?? battery?.pMaxD ?? battery?.power ?? battery?.Power,
      0
    );
    return sum + Math.max(0, count) * Math.max(0, powerKw);
  }, 0);
  return totalKw > 0 ? totalKw / 1000 : null;
}

export function normalizeTollingFixedCapacity(config = {}) {
  const hasCanonical =
    config?.tollingFixedCapacity && typeof config.tollingFixedCapacity === "object";
  const source = hasCanonical ? config.tollingFixedCapacity : {};
  const legacyPenaltyAnnual = parseOptionalFiniteNumber(config.tollingPenaltyAnnual);
  const sourceBankability =
    source.bankability && typeof source.bankability === "object" ? source.bankability : {};
  const sourcePerformance =
    source.performanceRegime && typeof source.performanceRegime === "object"
      ? source.performanceRegime
      : {};
  const creditSupportType =
    sourceBankability.creditSupportType ??
    sourceBankability.credit_support_type ??
    DEFAULT_TOLLING_FIXED_CAPACITY.bankability.creditSupportType;
  const penaltyMode =
    VALID_PENALTY_MODES.has(source.penaltyMode)
      ? source.penaltyMode
      : legacyPenaltyAnnual && legacyPenaltyAnnual > 0
        ? "fixed_annual_eur"
        : DEFAULT_TOLLING_FIXED_CAPACITY.penaltyMode;

  return {
    ...DEFAULT_TOLLING_FIXED_CAPACITY,
    ...source,
    feeInputMode: VALID_FEE_INPUT_MODES.has(source.feeInputMode)
      ? source.feeInputMode
      : DEFAULT_TOLLING_FIXED_CAPACITY.feeInputMode,
    feeAmount: hasCanonical && hasOwnValue(source, "feeAmount")
      ? parseOptionalFiniteNumber(source.feeAmount)
      : parseOptionalFiniteNumber(config.tollingFixedFeePerMWYear),
    settlementFrequency: normalizeSettlementFrequency(
      source.settlementFrequency ?? config.tollingSettlementFrequency
    ),
    contractTermYears: parseFiniteNumber(
      source.contractTermYears ?? config.tollingContractYears,
      DEFAULT_TOLLING_FIXED_CAPACITY.contractTermYears
    ),
    annualEscalationPct: parseFiniteNumber(
      source.annualEscalationPct ?? config.tollingEscalationPct,
      DEFAULT_TOLLING_FIXED_CAPACITY.annualEscalationPct
    ),
    capacityBasis: normalizeCapacityBasis(source.capacityBasis ?? config.tollingCapacityBasis),
    contractedCapacityMw: parseOptionalFiniteNumber(
      source.contractedCapacityMw ?? source.contracted_capacity_mw
    ),
    applyAvailabilityAdjustment:
      source.applyAvailabilityAdjustment ??
      source.apply_availability_adjustment ??
      DEFAULT_TOLLING_FIXED_CAPACITY.applyAvailabilityAdjustment,
    availabilityAdjustmentPct: parseOptionalFiniteNumber(
      source.availabilityAdjustmentPct ??
        source.availability_adjustment_pct ??
        config.tollingAvailabilityPct
    ),
    penaltyMode,
    annualPenaltyEur:
      penaltyMode === "fixed_annual_eur"
        ? parseOptionalFiniteNumber(source.annualPenaltyEur ?? config.tollingPenaltyAnnual)
        : parseOptionalFiniteNumber(source.annualPenaltyEur),
    penaltyPctOfGrossFee: parseOptionalFiniteNumber(source.penaltyPctOfGrossFee),
    penaltyPerSettlementPeriodEur: parseOptionalFiniteNumber(
      source.penaltyPerSettlementPeriodEur
    ),
    merchantOptimization:
      source.merchantOptimization ??
      source.merchant_optimization ??
      config.useMerchantOptimization ??
      DEFAULT_TOLLING_FIXED_CAPACITY.merchantOptimization,
    includedRevenueStreams: {
      ...DEFAULT_TOLLING_FIXED_CAPACITY.includedRevenueStreams,
      ...(source.includedRevenueStreams || source.included_revenue_streams || {}),
    },
    bankability: {
      ...DEFAULT_TOLLING_FIXED_CAPACITY.bankability,
      ...sourceBankability,
      counterpartyStatus:
        sourceBankability.counterpartyStatus ??
        sourceBankability.counterparty_status ??
        DEFAULT_TOLLING_FIXED_CAPACITY.bankability.counterpartyStatus,
      counterpartyType:
        sourceBankability.counterpartyType ??
        sourceBankability.counterparty_type ??
        DEFAULT_TOLLING_FIXED_CAPACITY.bankability.counterpartyType,
      counterpartyName:
        sourceBankability.counterpartyName ??
        sourceBankability.counterparty_name ??
        DEFAULT_TOLLING_FIXED_CAPACITY.bankability.counterpartyName,
      creditSupportType,
      creditSupportMonths:
        creditSupportType === "none"
          ? null
          : parseOptionalFiniteNumber(
              sourceBankability.creditSupportMonths ?? sourceBankability.credit_support_months
            ),
    },
    performanceRegime: {
      ...DEFAULT_TOLLING_FIXED_CAPACITY.performanceRegime,
      ...sourcePerformance,
      guaranteedAvailabilityPct: parseOptionalFiniteNumber(
        sourcePerformance.guaranteedAvailabilityPct ??
          sourcePerformance.guaranteed_availability_pct
      ),
      penaltyCapPctOfAnnualFee: parseOptionalFiniteNumber(
        sourcePerformance.penaltyCapPctOfAnnualFee ??
          sourcePerformance.penalty_cap_pct_of_annual_fee
      ),
      plannedOutageAllowanceHours: parseOptionalFiniteNumber(
        sourcePerformance.plannedOutageAllowanceHours ??
          sourcePerformance.planned_outage_allowance_hours
      ),
      guaranteedRtePct: parseOptionalFiniteNumber(
        sourcePerformance.guaranteedRtePct ?? sourcePerformance.guaranteed_rte_pct
      ),
      maxAnnualCycles: parseOptionalFiniteNumber(
        sourcePerformance.maxAnnualCycles ?? sourcePerformance.max_annual_cycles
      ),
      maxAnnualThroughputMWh: parseOptionalFiniteNumber(
        sourcePerformance.maxAnnualThroughputMWh ??
          sourcePerformance.max_annual_throughput_mwh
      ),
      degradationResponsibility:
        sourcePerformance.degradationResponsibility ??
        sourcePerformance.degradation_responsibility ??
        DEFAULT_TOLLING_FIXED_CAPACITY.performanceRegime.degradationResponsibility,
      augmentationResponsibility:
        sourcePerformance.augmentationResponsibility ??
        sourcePerformance.augmentation_responsibility ??
        DEFAULT_TOLLING_FIXED_CAPACITY.performanceRegime.augmentationResponsibility,
      chargingCostResponsibility:
        sourcePerformance.chargingCostResponsibility ??
        sourcePerformance.charging_cost_responsibility ??
        DEFAULT_TOLLING_FIXED_CAPACITY.performanceRegime.chargingCostResponsibility,
      dispatchRights:
        sourcePerformance.dispatchRights ??
        sourcePerformance.dispatch_rights ??
        DEFAULT_TOLLING_FIXED_CAPACITY.performanceRegime.dispatchRights,
    },
    fixedCapacityFeePreview: null,
  };
}

export function calculateFixedCapacityFeePreview(tollingFixedCapacity = {}, config = {}) {
  const normalized = normalizeTollingFixedCapacity({ ...config, tollingFixedCapacity });
  const installedCapacityMw = deriveInstalledBessPowerMw(config);
  const capacityMw =
    normalized.capacityBasis === "contracted_capacity_mw"
      ? Math.max(0, parseFiniteNumber(normalized.contractedCapacityMw, 0))
      : Math.max(0, parseFiniteNumber(installedCapacityMw, 0));
  const feeAmount = Math.max(0, parseFiniteNumber(normalized.feeAmount, 0));
  const periodsPerYear = getFixedCapacitySettlementPeriodsPerYear(
    normalized.settlementFrequency
  );

  let annualGrossFee = 0;
  if (normalized.feeInputMode === "eur_per_mw_year") {
    annualGrossFee = feeAmount * capacityMw;
  } else if (normalized.feeInputMode === "eur_per_mw_month") {
    annualGrossFee = feeAmount * capacityMw * 12;
  } else if (normalized.feeInputMode === "eur_per_kw_month") {
    annualGrossFee = feeAmount * capacityMw * 1000 * 12;
  } else if (normalized.feeInputMode === "absolute_per_year") {
    annualGrossFee = feeAmount;
  } else if (normalized.feeInputMode === "absolute_per_settlement_period") {
    annualGrossFee = feeAmount * periodsPerYear;
  }

  const availabilityFactor =
    normalized.applyAvailabilityAdjustment && normalized.availabilityAdjustmentPct != null
      ? clampPercent(normalized.availabilityAdjustmentPct, 100) / 100
      : 1;
  const annualFeeAfterAvailability = annualGrossFee * availabilityFactor;

  let annualPenalty = 0;
  if (normalized.penaltyMode === "fixed_annual_eur") {
    annualPenalty = Math.max(0, parseFiniteNumber(normalized.annualPenaltyEur, 0));
  } else if (normalized.penaltyMode === "percent_of_gross_fee") {
    annualPenalty =
      annualGrossFee * (Math.max(0, parseFiniteNumber(normalized.penaltyPctOfGrossFee, 0)) / 100);
  } else if (normalized.penaltyMode === "fixed_per_settlement_period") {
    annualPenalty =
      Math.max(0, parseFiniteNumber(normalized.penaltyPerSettlementPeriodEur, 0)) *
      periodsPerYear;
  }

  const annualNetFixedFee = Math.max(0, annualFeeAfterAvailability - annualPenalty);
  return {
    capacityMw,
    availabilityPct: availabilityFactor * 100,
    annualGrossFee,
    annualPenalty,
    annualNetFixedFee,
    grossFeePerSettlementPeriod: annualGrossFee / periodsPerYear,
    penaltyPerSettlementPeriod: annualPenalty / periodsPerYear,
    netFeePerSettlementPeriod: annualNetFixedFee / periodsPerYear,
    settlementFrequency: normalized.settlementFrequency,
  };
}

export function prepareTollingFixedCapacityForPersistence(config = {}) {
  const normalized = normalizeTollingFixedCapacity(config);
  const penaltyMode = normalized.penaltyMode;
  const bankability = normalized.bankability || DEFAULT_TOLLING_FIXED_CAPACITY.bankability;
  const performanceRegime =
    normalized.performanceRegime || DEFAULT_TOLLING_FIXED_CAPACITY.performanceRegime;

  return {
    ...normalized,
    feeAmount: parseOptionalFiniteNumber(normalized.feeAmount),
    applyAvailabilityAdjustment: Boolean(normalized.applyAvailabilityAdjustment),
    availabilityAdjustmentPct:
      normalized.availabilityAdjustmentPct == null
        ? null
        : clampPercent(normalized.availabilityAdjustmentPct, 100),
    fixedCapacityFeePreview: null,
    contractedCapacityMw: parseOptionalFiniteNumber(normalized.contractedCapacityMw),
    annualPenaltyEur:
      penaltyMode === "fixed_annual_eur"
        ? parseOptionalFiniteNumber(normalized.annualPenaltyEur)
        : null,
    penaltyPctOfGrossFee:
      penaltyMode === "percent_of_gross_fee"
        ? parseOptionalFiniteNumber(normalized.penaltyPctOfGrossFee)
        : null,
    penaltyPerSettlementPeriodEur:
      penaltyMode === "fixed_per_settlement_period"
        ? parseOptionalFiniteNumber(normalized.penaltyPerSettlementPeriodEur)
        : null,
    bankability: {
      ...DEFAULT_TOLLING_FIXED_CAPACITY.bankability,
      ...bankability,
      counterpartyName: String(bankability.counterpartyName ?? ""),
      creditSupportMonths:
        bankability.creditSupportType === "none"
          ? null
          : parseOptionalFiniteNumber(bankability.creditSupportMonths),
    },
    performanceRegime: {
      ...DEFAULT_TOLLING_FIXED_CAPACITY.performanceRegime,
      ...performanceRegime,
      guaranteedAvailabilityPct:
        performanceRegime.guaranteedAvailabilityPct == null
          ? null
          : clampPercent(performanceRegime.guaranteedAvailabilityPct, 0),
      penaltyCapPctOfAnnualFee:
        performanceRegime.penaltyCapPctOfAnnualFee == null
          ? null
          : Math.max(0, parseFiniteNumber(performanceRegime.penaltyCapPctOfAnnualFee, 0)),
      plannedOutageAllowanceHours:
        performanceRegime.plannedOutageAllowanceHours == null
          ? null
          : Math.max(0, parseFiniteNumber(performanceRegime.plannedOutageAllowanceHours, 0)),
      guaranteedRtePct:
        performanceRegime.guaranteedRtePct == null
          ? null
          : clampPercent(performanceRegime.guaranteedRtePct, 0),
      maxAnnualCycles:
        performanceRegime.maxAnnualCycles == null
          ? null
          : Math.max(0, parseFiniteNumber(performanceRegime.maxAnnualCycles, 0)),
      maxAnnualThroughputMWh:
        performanceRegime.maxAnnualThroughputMWh == null
          ? null
          : Math.max(0, parseFiniteNumber(performanceRegime.maxAnnualThroughputMWh, 0)),
    },
  };
}

export function normalizeProjectTollingFixedCapacity(config = {}) {
  const tollingFixedCapacity = normalizeTollingFixedCapacity(config);
  return {
    ...config,
    tollingFixedCapacity,
    tollingFixedFeePerMWYear: tollingFixedCapacity.feeAmount,
    tollingContractYears: tollingFixedCapacity.contractTermYears,
    tollingAvailabilityPct: tollingFixedCapacity.availabilityAdjustmentPct,
    tollingPenaltyAnnual: tollingFixedCapacity.annualPenaltyEur ?? 0,
    tollingSettlementFrequency: tollingFixedCapacity.settlementFrequency,
    tollingEscalationPct: tollingFixedCapacity.annualEscalationPct,
    tollingCapacityBasis: toLegacyCapacityBasis(tollingFixedCapacity.capacityBasis),
    useMerchantOptimization: Boolean(tollingFixedCapacity.merchantOptimization),
  };
}

export function prepareProjectTollingFixedCapacityForPersistence(config = {}) {
  const tollingFixedCapacity = prepareTollingFixedCapacityForPersistence(config);
  return {
    ...config,
    tollingFixedCapacity,
    tollingFixedFeePerMWYear: tollingFixedCapacity.feeAmount,
    tollingContractYears: tollingFixedCapacity.contractTermYears,
    tollingAvailabilityPct: tollingFixedCapacity.availabilityAdjustmentPct,
    tollingPenaltyAnnual: tollingFixedCapacity.annualPenaltyEur ?? 0,
    tollingSettlementFrequency: tollingFixedCapacity.settlementFrequency,
    tollingEscalationPct: tollingFixedCapacity.annualEscalationPct,
    tollingCapacityBasis: toLegacyCapacityBasis(tollingFixedCapacity.capacityBasis),
    useMerchantOptimization: Boolean(tollingFixedCapacity.merchantOptimization),
  };
}
